import React from 'react';
import * as Icons from '@ant-design/icons';

export const SPACE_ADDON_NOWRAP_STYLE: React.CSSProperties = {
  whiteSpace: 'nowrap',
  flex: 'none',
};

/**
 * 将 Ant Design Icons 组件名解析为 React 节点；找不到组件时返回 undefined。
 */
export function resolveIconNode(iconName?: string): React.ReactNode {
  const name = typeof iconName === 'string' ? iconName.trim() : '';
  if (!name) return undefined;
  const IconComponent = (Icons as unknown as Record<string, React.ComponentType<unknown>>)[name];
  if (!IconComponent) return undefined;
  return React.createElement(IconComponent);
}

export interface ResolveAffixPairOptions {
  /** 设计器中的图标名（如 SearchOutlined） */
  iconName?: string;
  /** 前置/后置标签文案 */
  label?: string;
}

/**
 * 单侧 affix：1) 有效图标名 → 图标（忽略 label）；2) 否则非空 label；3) 否则非空但无效的 icon 字符串 → 纯文本（兼容旧数据）。
 */
export function resolveAffixPair({
  iconName,
  label,
}: ResolveAffixPairOptions): React.ReactNode {
  const raw = typeof iconName === 'string' ? iconName.trim() : '';
  const labelStr =
    label === null || label === undefined
      ? ''
      : typeof label === 'string'
        ? label.trim()
        : String(label).trim();

  if (raw) {
    const IconComponent = (Icons as unknown as Record<string, React.ComponentType<unknown>>)[raw];
    if (IconComponent) {
      return React.createElement(IconComponent);
    }
  }
  if (labelStr) {
    return labelStr;
  }
  if (raw) {
    return raw;
  }
  return undefined;
}

export interface AffixRenderConfig {
  iconNode?: React.ReactNode;
  iconConfigured: boolean;
  labelText?: string;
}

/**
 * 将单侧配置拆分为：图标（或无效图标名的回退文本）、是否配置了图标、纯标签文案。
 * 组件层可据此实现“图标优先，标签用 Space.Addon”。
 */
export function resolveAffixRenderConfig({
  iconName,
  label,
}: ResolveAffixPairOptions): AffixRenderConfig {
  const rawIcon = typeof iconName === 'string' ? iconName.trim() : '';
  const rawLabel =
    label === null || label === undefined
      ? ''
      : typeof label === 'string'
        ? label.trim()
        : String(label).trim();

  if (rawIcon) {
    const iconNode = resolveIconNode(rawIcon);
    return {
      iconConfigured: true,
      iconNode: iconNode ?? rawIcon,
      labelText: rawLabel || undefined,
    };
  }

  return {
    iconConfigured: false,
    iconNode: undefined,
    labelText: rawLabel || undefined,
  };
}
